import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import type { ActivityRegistrationDocSignatureRecord } from "../../../api/activityRegistrationDocs";
import { showUserNotice } from "../../../api/errors";

type Props = {
  orgLabel: string;
  activityTitle: string;
  memberName: string;
  documents: ActivityRegistrationDocSignatureRecord[];
  initialIndex?: number;
  onClose: () => void;
};

export default function ActivityRegistrationDocSignPreviewModal({
  orgLabel,
  activityTitle,
  memberName,
  documents,
  initialIndex = 0,
  onClose,
}: Props) {
  const [step, setStep] = useState(initialIndex);
  const [sigImgFailed, setSigImgFailed] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const current = documents[step] ?? null;
  const total = documents.length;
  const sigUrl = current?.signatureDataUrl?.trim() || null;

  useEffect(() => {
    setStep(Math.min(initialIndex, Math.max(0, documents.length - 1)));
  }, [documents, initialIndex]);

  useEffect(() => {
    setSigImgFailed(false);
  }, [sigUrl, step]);

  const exportPdf = async () => {
    const el = printRef.current;
    if (!el || !current) return;
    setPdfExporting(true);
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const pageWPt = 595.28;
      const margin = 40;
      const imgW = pageWPt - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const pdfH = Math.max(841.89, imgH + margin * 2);
      const pdf = new jsPDF({ unit: "pt", format: [pageWPt, pdfH], orientation: "portrait" });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(imgData, "JPEG", margin, margin, imgW, imgH, undefined, "FAST");
      const safeName = memberName.replace(/[/\\?%*:|"<>]/g, "_").trim().slice(0, 32) || "会员";
      const safeDoc = (current.fileName || "签署文件").replace(/[/\\?%*:|"<>]/g, "_").slice(0, 24);
      pdf.save(`活动报名签署-${safeName}-${safeDoc}.pdf`);
    } catch {
      showUserNotice("导出 PDF 失败，请稍后重试。若含签名图，请确认图片地址允许跨域加载。");
    } finally {
      setPdfExporting(false);
    }
  };

  if (!current) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-sm text-gray-500">暂无签署存档</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 px-5 py-2 bg-red-600 text-white text-sm rounded-xl cursor-pointer"
          >
            关闭
          </button>
        </div>
      </div>
    );
  }

  const o = orgLabel.trim() || "本协会";
  const showSigPhoto = Boolean(sigUrl && !sigImgFailed);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-indigo-600">
          <div className="flex items-center gap-3 min-w-0">
            <i className="ri-file-shield-2-line text-white text-xl flex-shrink-0"></i>
            <div className="min-w-0">
              <h3 className="font-bold text-white text-sm truncate">《{current.fileName || "签署文件"}》</h3>
              <p className="text-indigo-200 text-xs mt-0.5 truncate">
                {activityTitle} · {memberName}
                {total > 1 ? ` · ${step + 1}/${total}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-indigo-500 cursor-pointer text-white flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 text-sm text-gray-700 leading-relaxed space-y-4">
          <div ref={printRef} className="space-y-4 bg-white">
            <div className="border-b border-gray-100 pb-4">
              <h2 className="text-center text-base font-bold text-gray-900">{current.fileName || "活动报名签署文件"}</h2>
              <div className="mt-3 space-y-1 text-xs leading-relaxed text-gray-600 text-center">
                <p>活动：{activityTitle}</p>
                <p>签署人：{memberName}</p>
                <p>协会：{o}</p>
                {current.version ? <p className="text-gray-500">版本：{current.version}</p> : null}
                {current.signedAt ? <p className="text-gray-500">签署时间：{current.signedAt}</p> : null}
              </div>
            </div>
            <div className="whitespace-pre-wrap text-gray-700">{current.content || "（无存档正文）"}</div>
            <div className="border-t border-gray-200 pt-5 mt-4">
              <p className="text-xs text-gray-500 mb-3 text-center">
                以下为报名时的电子签名存档，与当时文件正文版本绑定。
              </p>
              <div className="flex items-end justify-center gap-6 flex-wrap">
                <div className="flex-1 min-w-[200px] max-w-sm">
                  <p className="text-xs text-gray-400 mb-1">电子签名</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-center overflow-hidden h-20 box-border">
                    {showSigPhoto ? (
                      <img
                        key={sigUrl ?? "sig"}
                        src={sigUrl!}
                        alt={`${memberName} 的电子签名`}
                        className="max-h-full max-w-full w-auto object-contain"
                        onError={() => setSigImgFailed(true)}
                      />
                    ) : (
                      <span className="text-xs text-gray-400">无签名图</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={step <= 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="px-3 py-2 border border-gray-300 bg-white text-gray-800 text-sm rounded-xl cursor-pointer disabled:opacity-40"
            >
              上一份
            </button>
            <button
              type="button"
              disabled={step >= total - 1}
              onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              className="px-3 py-2 border border-gray-300 bg-white text-gray-800 text-sm rounded-xl cursor-pointer disabled:opacity-40"
            >
              下一份
            </button>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              disabled={pdfExporting}
              onClick={() => void exportPdf()}
              className="px-4 py-2 border border-gray-300 bg-white text-gray-800 text-sm rounded-xl cursor-pointer hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {pdfExporting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-file-pdf-line" />}
              {pdfExporting ? "生成中…" : "导出 PDF"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-xl cursor-pointer hover:bg-indigo-700 flex items-center justify-center"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
